import React, { useState, useEffect } from "react";
import SupportEmail from "../components/SupportEmail";
// Import the waitlist-specific CSS
import "../styles/waitlist-styles.css";

const Contact = () => {
  const [step, setStep] = useState('form'); // 'form', 'verify', 'success', 'error'
  const [formData, setFormData] = useState({ email: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');


  const API_BASE_URL = 'https://1geg9kws47.execute-api.us-east-1.amazonaws.com/default/OmegaVoidWaitlist';

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Submit to your Lambda function via API Gateway
      const response = await fetch(`${API_BASE_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          verificationMethod: 'link' // Always use email link
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit. Please try again.');
      }

      setStep('verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLinkVerification = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        throw new Error('Invalid or expired verification link.');
      }

      setStep('success');
    } catch (err) {
      setStep('error');
      setError(err.message);
    }
  };

  // Check URL params for verification token on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      handleEmailLinkVerification(token);
    }
  }, []);

  const renderWaitlistForm = () => (
    <div className="waitlist-section highlight">
      <h2>🚀 Join the Omega Void Waitlist</h2>
      <p className="waitlist-description">
        Experience the world's first privacy-first communication tool with minimal data retention. 
        Join our exclusive 4-month early access — free, encrypted, and only available for a limited time.
      </p>

      <div className="waitlist-form">
        <form onSubmit={handleFormSubmit}>
          <div className="waitlist-form-group">
            <label htmlFor="waitlist-email">Email Address *</label>
            <input
              type="email"
              id="waitlist-email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="your@email.com"
              className="waitlist-form-input"
            />
          </div>

          <div className="waitlist-form-group">
            <label htmlFor="waitlist-name">Name (Optional)</label>
            <input
              type="text"
              id="waitlist-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your name"
              className="waitlist-form-input"
            />
          </div>

          {error && <div className="waitlist-error">{error}</div>}

          <button 
            type="submit"
            disabled={loading || !formData.email} 
            className="waitlist-button"
          >
            {loading ? (
              <span className="loading-spinner">⏳ Processing...</span>
            ) : (
              'Join the Waitlist'
            )}
          </button>
        </form>
      </div>
    </div>
  );

  const renderWaitlistVerification = () => (
    <div className="waitlist-section highlight">
      <h2>📧 Check Your Email</h2>
      
      <div className="waitlist-verification">
        <h3>Verification Link Sent</h3>
        <p>
          We've sent a verification link to <strong>{formData.email}</strong>
        </p>
        <p>
          Click the link in your email to complete your waitlist registration.
        </p>
        
        <div className="verification-note">
          <p>
            📧 Check your spam folder if you don't see the email within a few minutes.
          </p>
        </div>
      </div>
      
      <button 
        onClick={() => setStep('form')} 
        className="waitlist-back-button"
      >
        ← Back to Form
      </button>
    </div>
  );

  const renderWaitlistSuccess = () => (
    <div className="waitlist-section waitlist-success">
      <div className="waitlist-success-icon">✅</div>
      <h3>Welcome to the Void!</h3>
      
      <div className="waitlist-success-content">
        <div>
          <p>
            <strong>Thank you!</strong> You've been added to the waitlist. Your unique ID will be emailed to you, 
            giving you of full, free access starting from that date.
          </p>
          {/* <p>
            This email will not be connected to your future purchases with Omega Void™ and will be 
            removed from the database after 4 months from receiving your unique Login ID.
          </p> */}
        </div>
        
        <div className="waitlist-next-steps">
          <h4>What's Next?</h4>
          <ul className="waitlist-steps-list">
            <li>Watch for an email with your unique access ID</li>
            <li>Your 4-month free access begins when you receive the ID</li>
            <li>No payment information required</li>
            <li>Complete privacy - your data is never stored long-term</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderWaitlistError = () => (
    <div className="waitlist-section waitlist-error-state">
      <div className="waitlist-error-icon">❌</div>
      <h3>Verification Failed</h3>
      
      <div className="waitlist-error-content">
        <p>{error}</p>
        <button 
          onClick={() => {
            setStep('form');
            setError('');
          }} 
          className="waitlist-retry-button"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  const renderWaitlistSection = () => {
    switch (step) {
      case 'form':
        return renderWaitlistForm();
      case 'verify':
        return renderWaitlistVerification();
      case 'success':
        return renderWaitlistSuccess();
      case 'error':
        return renderWaitlistError();
      default:
        return renderWaitlistForm();
    }
  };

  return (
    <>
      <div className="contact-container">
        <h1>Contact </h1>
        
        {/* Waitlist Section */}
        {/* {renderWaitlistSection()} */}
        
        {/* Existing Contact Sections */}
        <div className="contact-section general">
          <h2>General Inquiries</h2>
          <p>For questions, support, or information about our services:</p>
          <a href="mailto:support@omegavoid.com" className="email-link">
            support@omegavoid.com
          </a>
        </div>
        
        <div className="contact-section corporate">
          <div className="verified-badge">
            <span className="verified-icon">✓</span> Verified Corporate Channel
          </div>
          
          <h2>Corporate and Partnership Verification</h2>
          <p className="corporate-message">
            If you are a business, government agency, or law enforcement representative 
            seeking official documentation, prototypes, or licensing, please verify 
            correspondence only with:
          </p>
          
          <a href="mailto:corporate@omegaopscyberdefence.com" className="email-link corporate-email">
            corporate@omegaopscyberdefence.com
          </a>
          
          <div className="security-note">
            <p>
              <strong>Security Notice:</strong> For security verification purposes, all official 
              communication will be conducted exclusively through this email address.
            </p>
          </div>
        </div>
      </div>
      <SupportEmail />
    </>
  );
};

export default Contact;